export interface MarketData {
  slug: string;
  name: string;
  state: string;
  stateAbbr: string;
  region?: string;
  metro?: string;
  searchLocation?: string;
  summary: string;
  buyerSummary: string;
  workerSummary: string;
  localInsight: string;
  venues: string[];
  eventTypes: string[];
  topRoles: string[];
  topSpecialties: string[];
  nearbyMarkets: string[];
}

export interface VendorSpecialty {
  slug: string;
  apiValue: string;
  label: string;
  pluralLabel: string;
  buyerIntent: string;
  description: string;
}

export interface HireRole {
  slug: string;
  gigSlug: string;
  label: string;
  pluralLabel: string;
  searchRole: string;
  dayRate: string;
  description: string;
  buyerNeed: string;
}

export const VENDOR_SPECIALTIES: Record<string, VendorSpecialty> = {
  'av-rental': {
    slug: 'av-rental',
    apiValue: 'av_rental',
    label: 'AV Rental',
    pluralLabel: 'AV rental companies',
    buyerIntent: 'equipment, technicians, and show support',
    description: 'Find AV rental companies for corporate events, conferences, concerts, and live productions.',
  },
  'union-labor': {
    slug: 'union-labor',
    apiValue: 'union_labor',
    label: 'Union Labor',
    pluralLabel: 'union labor providers',
    buyerIntent: 'IATSE and union crew calls',
    description: 'Find union-affiliated labor providers for venues, tours, theater, and convention work.',
  },
  audio: {
    slug: 'audio',
    apiValue: 'audio',
    label: 'Audio',
    pluralLabel: 'audio companies',
    buyerIntent: 'FOH, monitor, RF, and PA support',
    description: 'Find audio companies for live sound, corporate general sessions, concerts, and broadcast work.',
  },
  lighting: {
    slug: 'lighting',
    apiValue: 'lighting',
    label: 'Lighting',
    pluralLabel: 'lighting companies',
    buyerIntent: 'fixtures, consoles, design, and lighting crews',
    description: 'Find lighting companies for stage lighting, corporate events, concerts, and brand activations.',
  },
  video: {
    slug: 'video',
    apiValue: 'video',
    label: 'Video',
    pluralLabel: 'video production companies',
    buyerIntent: 'switching, cameras, LED, and projection',
    description: 'Find video vendors for IMAG, livestreams, LED walls, projection, and multi-camera shows.',
  },
  staging: {
    slug: 'staging',
    apiValue: 'staging',
    label: 'Staging',
    pluralLabel: 'staging companies',
    buyerIntent: 'stages, risers, truss, and scenic builds',
    description: 'Find staging companies for concerts, festivals, trade shows, and corporate productions.',
  },
  backline: {
    slug: 'backline',
    apiValue: 'backline',
    label: 'Backline',
    pluralLabel: 'backline rental companies',
    buyerIntent: 'instruments, amps, keyboards, and techs',
    description: 'Find backline rental companies for tours, festivals, worship events, and live music.',
  },
  production: {
    slug: 'production',
    apiValue: 'production',
    label: 'Production',
    pluralLabel: 'event production companies',
    buyerIntent: 'full production management and technical staffing',
    description: 'Find event production companies for planning, technical direction, and show execution.',
  },
};

export const HIRE_ROLES: Record<string, HireRole> = {
  stagehands: {
    slug: 'stagehands',
    gigSlug: 'stagehand',
    label: 'Stagehand',
    pluralLabel: 'stagehands',
    searchRole: 'stagehand',
    dayRate: 'Rates vary; verify each listing.',
    description: 'Load-in, load-out, set/strike, cases, staging, and general live event labor.',
    buyerNeed: 'general hands for load-in, show calls, changeovers, and load-out.',
  },
  'av-technicians': {
    slug: 'av-technicians',
    gigSlug: 'av-technician',
    label: 'AV Technician',
    pluralLabel: 'AV technicians',
    searchRole: 'av tech',
    dayRate: 'Rates vary; verify each listing.',
    description: 'Corporate AV techs for breakout rooms, general sessions, hotel AV, and convention work.',
    buyerNeed: 'technicians who can support audio, video, projection, comms, and client-facing rooms.',
  },
  'audio-engineers': {
    slug: 'audio-engineers',
    gigSlug: 'audio-engineer',
    label: 'Audio Engineer',
    pluralLabel: 'audio engineers',
    searchRole: 'audio engineer',
    dayRate: 'Rates vary; verify each listing.',
    description: 'A1 engineers for FOH, monitors, RF coordination, system tuning, and show operation.',
    buyerNeed: 'lead audio coverage for live music, corporate general sessions, and broadcast-style events.',
  },
  'audio-technicians': {
    slug: 'audio-technicians',
    gigSlug: 'audio-technician',
    label: 'Audio Technician',
    pluralLabel: 'audio technicians',
    searchRole: 'audio technician',
    dayRate: 'Rates vary; verify each listing.',
    description: 'A2 support for patching, microphones, RF, cable runs, and audio setup.',
    buyerNeed: 'hands-on audio support for setup, strike, stage patching, and show support.',
  },
  'lighting-technicians': {
    slug: 'lighting-technicians',
    gigSlug: 'lighting-technician',
    label: 'Lighting Technician',
    pluralLabel: 'lighting technicians',
    searchRole: 'lighting technician',
    dayRate: 'Rates vary; verify each listing.',
    description: 'L2 crew for fixtures, cable, focus, dimmers, power, and followspot work.',
    buyerNeed: 'lighting support for corporate shows, concerts, theater, and trade show booths.',
  },
  'video-technicians': {
    slug: 'video-technicians',
    gigSlug: 'video-technician',
    label: 'Video Technician',
    pluralLabel: 'video technicians',
    searchRole: 'video technician',
    dayRate: 'Rates vary; verify each listing.',
    description: 'V2 techs for LED walls, projection, cameras, SDI/fiber runs, and show support.',
    buyerNeed: 'video support for LED, projection, IMAG, switching support, and breakout rooms.',
  },
  riggers: {
    slug: 'riggers',
    gigSlug: 'rigger',
    label: 'Rigger',
    pluralLabel: 'riggers',
    searchRole: 'rigger',
    dayRate: 'Rates vary; verify each listing.',
    description: 'Arena and theater riggers for truss, motors, bridles, fall protection, and safe overhead work.',
    buyerNeed: 'qualified rigging crew for arenas, ballrooms, festivals, and touring productions.',
  },
  'led-technicians': {
    slug: 'led-technicians',
    gigSlug: 'led-technician',
    label: 'LED Technician',
    pluralLabel: 'LED technicians',
    searchRole: 'led technician',
    dayRate: 'Rates vary; verify each listing.',
    description: 'LED wall techs for tile builds, processors, mapping, calibration, and maintenance.',
    buyerNeed: 'LED wall support for corporate stages, concerts, trade shows, and broadcast environments.',
  },
  'camera-operators': {
    slug: 'camera-operators',
    gigSlug: 'camera-operator',
    label: 'Camera Operator',
    pluralLabel: 'camera operators',
    searchRole: 'camera operator',
    dayRate: 'Rates vary; verify each listing.',
    description: 'Camera operators for IMAG, livestreams, broadcast, corporate events, and concerts.',
    buyerNeed: 'camera coverage for IMAG, broadcast, livestream, and recorded event work.',
  },
  'production-assistants': {
    slug: 'production-assistants',
    gigSlug: 'production-assistant',
    label: 'Production Assistant',
    pluralLabel: 'production assistants',
    searchRole: 'production assistant',
    dayRate: 'Rates vary; verify each listing.',
    description: 'Production support for check-in, logistics, credentials, errands, and department coordination.',
    buyerNeed: 'detail-oriented production support for busy event days and multi-day shows.',
  },
};

export const GIG_ROLES: Record<string, HireRole> = Object.values(HIRE_ROLES).reduce((acc, role) => {
  acc[role.gigSlug] = role;
  return acc;
}, {} as Record<string, HireRole>);

export const MARKET_DATA: Record<string, MarketData> = {
  'inland-empire': {
    slug: 'inland-empire',
    name: 'Inland Empire',
    state: 'California',
    stateAbbr: 'CA',
    region: 'inland-empire',
    metro: 'inland-empire',
    summary: 'Riverside, Ontario, San Bernardino, Palm Springs, and the Coachella Valley make the Inland Empire a standalone event market.',
    buyerSummary: 'Find AV companies, production vendors, and event crew serving Inland Empire arenas, casinos, conventions, and touring stops.',
    workerSummary: 'Browse stagehand, AV technician, audio, lighting, video, and rigging gigs across the Inland Empire.',
    localInsight: 'This market often gets buried under LA or Orange County. Treating it separately helps local crew and businesses get found for Inland Empire searches.',
    venues: ['Toyota Arena', 'Riverside Convention Center', 'Yaamava Resort & Casino', 'Acrisure Arena', 'Ontario Convention Center'],
    eventTypes: ['Arena concerts', 'Casino entertainment', 'Convention work', 'Corporate events', 'Festival load-ins'],
    topRoles: ['stagehands', 'av-technicians', 'audio-engineers', 'lighting-technicians', 'riggers'],
    topSpecialties: ['audio'],
    nearbyMarkets: ['los-angeles', 'orange-county', 'anaheim', 'las-vegas'],
  },
  riverside: {
    slug: 'riverside',
    name: 'Riverside',
    state: 'California',
    stateAbbr: 'CA',
    searchLocation: 'Riverside',
    summary: 'Riverside anchors Inland Empire convention, theater, university, corporate, and touring event work.',
    buyerSummary: 'Find AV companies, staging vendors, production teams, and local event crew serving Riverside venues and nearby Inland Empire calls.',
    workerSummary: 'Browse Riverside stagehand, AV technician, audio, lighting, video, and production gigs.',
    localInsight: 'Riverside searches should connect to Inland Empire inventory without hiding the city-specific intent that buyers use in Google.',
    venues: ['Riverside Convention Center', 'Fox Performing Arts Center', 'Riverside Municipal Auditorium', 'UC Riverside Events', 'Downtown Riverside Venues'],
    eventTypes: ['Convention work', 'Corporate events', 'Theater', 'University events', 'Touring stops'],
    topRoles: ['stagehands', 'av-technicians', 'audio-engineers', 'lighting-technicians', 'production-assistants'],
    topSpecialties: ['av-rental', 'production', 'lighting', 'audio', 'staging'],
    nearbyMarkets: ['inland-empire', 'ontario', 'palm-springs', 'orange-county'],
  },
  ontario: {
    slug: 'ontario',
    name: 'Ontario',
    state: 'California',
    stateAbbr: 'CA',
    searchLocation: 'Ontario',
    summary: 'Ontario sits at the center of Inland Empire arena, convention, hotel, and touring event demand.',
    buyerSummary: 'Find AV vendors, staging companies, and crew near Toyota Arena, Ontario Convention Center, and nearby Inland Empire venues.',
    workerSummary: 'Browse Ontario stagehand, AV, rigging, lighting, audio, and convention crew gigs.',
    localInsight: 'Ontario is a strong city-specific page because buyers often search by venue area instead of the broader Inland Empire label.',
    venues: ['Toyota Arena', 'Ontario Convention Center', 'Ontario Airport Hotels', 'Downtown Ontario Venues', 'Rancho Cucamonga Event Spaces'],
    eventTypes: ['Arena concerts', 'Convention work', 'Corporate events', 'Hotel AV', 'Touring shows'],
    topRoles: ['stagehands', 'av-technicians', 'riggers', 'audio-engineers', 'lighting-technicians'],
    topSpecialties: ['av-rental', 'staging', 'lighting', 'audio', 'union-labor'],
    nearbyMarkets: ['inland-empire', 'riverside', 'orange-county', 'los-angeles'],
  },
  'palm-springs': {
    slug: 'palm-springs',
    name: 'Palm Springs',
    state: 'California',
    stateAbbr: 'CA',
    searchLocation: 'Palm Springs',
    summary: 'Palm Springs and the Coachella Valley create seasonal demand for festivals, resort events, corporate retreats, casinos, and arena shows.',
    buyerSummary: 'Find AV companies, staging vendors, audio providers, and crew serving Palm Springs, Indio, Coachella Valley, and desert resort events.',
    workerSummary: 'Browse Palm Springs stagehand, AV, audio, lighting, video, and festival support gigs.',
    localInsight: 'Desert event searches often happen by Palm Springs or Coachella Valley, so this page captures a different intent than Riverside or Ontario.',
    venues: ['Palm Springs Convention Center', 'Acrisure Arena', 'Empire Polo Club', 'Agua Caliente Casinos', 'Coachella Valley Resorts'],
    eventTypes: ['Festivals', 'Resort events', 'Corporate retreats', 'Casino entertainment', 'Arena concerts'],
    topRoles: ['stagehands', 'av-technicians', 'audio-engineers', 'lighting-technicians', 'video-technicians'],
    topSpecialties: ['audio', 'lighting', 'staging', 'av-rental', 'production'],
    nearbyMarkets: ['inland-empire', 'riverside', 'ontario', 'orange-county'],
  },
  'los-angeles': {
    slug: 'los-angeles',
    name: 'Los Angeles',
    state: 'California',
    stateAbbr: 'CA',
    region: 'la-metro',
    metro: 'los-angeles',
    summary: 'Los Angeles listings can span film, TV, awards, concerts, brand activations, and corporate productions.',
    buyerSummary: 'Find event production companies, AV vendors, and crew for LA venues, studios, hotels, and large-scale productions.',
    workerSummary: 'Browse LA gigs for stagehands, AV techs, audio, lighting, video, production assistants, and riggers.',
    localInsight: 'Awards season, festival season, studio work, and corporate launches all stack together, so LA needs both specialty crew and fast general labor coverage.',
    venues: ['Crypto.com Arena', 'SoFi Stadium', 'LA Convention Center', 'Hollywood Bowl', 'Dolby Theatre'],
    eventTypes: ['Awards shows', 'Film and TV production', 'Concerts', 'Corporate events', 'Brand activations'],
    topRoles: ['stagehands', 'av-technicians', 'audio-engineers', 'video-technicians', 'production-assistants'],
    topSpecialties: ['production', 'av-rental', 'audio', 'lighting', 'video'],
    nearbyMarkets: ['orange-county', 'inland-empire', 'anaheim', 'san-francisco'],
  },
  'orange-county': {
    slug: 'orange-county',
    name: 'Orange County',
    state: 'California',
    stateAbbr: 'CA',
    region: 'oc',
    metro: 'orange-county',
    summary: 'Orange County has its own convention, resort, corporate, theme park, and entertainment event calendar.',
    buyerSummary: 'Find AV companies, staging vendors, and event crew serving Anaheim, Irvine, Costa Mesa, Huntington Beach, and Santa Ana.',
    workerSummary: 'Browse OC stagehand and AV gigs near convention centers, arenas, corporate campuses, resorts, and outdoor events.',
    localInsight: 'Orange County listings cover a distinct set of convention, arena, resort, corporate, and outdoor venues. Verify the exact work location before applying.',
    venues: ['Anaheim Convention Center', 'Honda Center', 'Angel Stadium', 'Segerstrom Center for the Arts', 'House of Blues Anaheim'],
    eventTypes: ['Conventions', 'Theme park events', 'Corporate events', 'Concerts', 'Beach events'],
    topRoles: ['stagehands', 'av-technicians', 'audio-engineers', 'lighting-technicians', 'led-technicians'],
    topSpecialties: ['av-rental', 'staging', 'lighting', 'video', 'production'],
    nearbyMarkets: ['anaheim', 'los-angeles', 'inland-empire', 'san-diego'],
  },
  anaheim: {
    slug: 'anaheim',
    name: 'Anaheim',
    state: 'California',
    stateAbbr: 'CA',
    searchLocation: 'Anaheim',
    summary: 'Anaheim hosts convention, arena, resort, theme park, and entertainment events across Southern California.',
    buyerSummary: 'Find AV rental companies, staging vendors, and event crew near the Anaheim Convention Center, Honda Center, and resort district.',
    workerSummary: 'Browse Anaheim gigs for convention load-ins, corporate AV, concerts, theme park events, and trade shows.',
    localInsight: 'Anaheim event calendars can include trade shows, fan conventions, arena events, and resort productions. Verify current dates and crew requirements with each listing.',
    venues: ['Anaheim Convention Center', 'Honda Center', 'Angel Stadium', 'House of Blues Anaheim', 'Disneyland Resort Events'],
    eventTypes: ['Trade shows', 'Theme park events', 'Concerts', 'Corporate events', 'Sports events'],
    topRoles: ['stagehands', 'av-technicians', 'riggers', 'audio-engineers', 'led-technicians'],
    topSpecialties: ['av-rental', 'staging', 'lighting', 'video', 'union-labor'],
    nearbyMarkets: ['orange-county', 'los-angeles', 'inland-empire', 'san-diego'],
  },
  'las-vegas': {
    slug: 'las-vegas',
    name: 'Las Vegas',
    state: 'Nevada',
    stateAbbr: 'NV',
    metro: 'las-vegas',
    summary: 'Las Vegas runs on residencies, conventions, casino entertainment, touring shows, and corporate productions.',
    buyerSummary: 'Find AV companies, staging vendors, riggers, show labor, and production crew across the Strip and Las Vegas valley.',
    workerSummary: 'Browse Las Vegas stagehand and AV gigs for conventions, concerts, residencies, fight nights, and corporate events.',
    localInsight: 'Las Vegas listings can span convention work, casino entertainment, touring shows, and specialized technical calls. Verify current volume and requirements with each source.',
    venues: ['Las Vegas Convention Center', 'Mandalay Bay Convention Center', 'T-Mobile Arena', 'Allegiant Stadium', 'MGM Grand'],
    eventTypes: ['Trade shows', 'Residencies', 'Corporate events', 'Concerts', 'Fight nights'],
    topRoles: ['stagehands', 'av-technicians', 'riggers', 'audio-engineers', 'lighting-technicians'],
    topSpecialties: ['av-rental', 'staging', 'lighting', 'audio', 'union-labor'],
    nearbyMarkets: ['inland-empire', 'los-angeles', 'orange-county', 'phoenix'],
  },
  orlando: {
    slug: 'orlando',
    name: 'Orlando',
    state: 'Florida',
    stateAbbr: 'FL',
    metro: 'orlando',
    summary: 'Orlando is a major convention, theme park, corporate AV, and entertainment production market.',
    buyerSummary: 'Find AV companies, event production vendors, and crew serving OCCC, theme parks, hotels, and corporate shows.',
    workerSummary: 'Browse Orlando AV, LED, stagehand, audio, video, and rigging gigs across the convention and attraction market.',
    localInsight: 'Orlando listings can include convention, hotel, theme park, and corporate AV work. Verify current dates, requirements, and venue access details with each source.',
    venues: ['Orange County Convention Center', 'Universal Studios Events', 'Walt Disney World Events', 'Kia Center', 'Dr. Phillips Center'],
    eventTypes: ['Trade shows', 'Theme park events', 'Corporate conferences', 'Concerts', 'Brand activations'],
    topRoles: ['av-technicians', 'stagehands', 'led-technicians', 'video-technicians', 'audio-engineers'],
    topSpecialties: ['av-rental', 'video', 'lighting', 'staging', 'production'],
    nearbyMarkets: ['miami', 'tampa', 'atlanta', 'nashville'],
  },
  nashville: {
    slug: 'nashville',
    name: 'Nashville',
    state: 'Tennessee',
    stateAbbr: 'TN',
    metro: 'nashville',
    summary: 'Nashville combines live music, award shows, church production, touring, and corporate events.',
    buyerSummary: 'Find audio companies, backline vendors, staging providers, and event crew for Nashville shows and corporate productions.',
    workerSummary: 'Browse Nashville gigs for audio engineers, stagehands, backline techs, lighting techs, and production support.',
    localInsight: 'Nashville listings can include audio, backline, award-show, festival, touring, and corporate-event work. Verify current inventory and requirements with each source.',
    venues: ['Bridgestone Arena', 'Ryman Auditorium', 'Grand Ole Opry', 'Music City Center', 'Ascend Amphitheater'],
    eventTypes: ['Live music', 'Award shows', 'Corporate events', 'Church events', 'Festivals'],
    topRoles: ['audio-engineers', 'stagehands', 'lighting-technicians', 'audio-technicians', 'production-assistants'],
    topSpecialties: ['audio', 'backline', 'staging', 'lighting', 'production'],
    nearbyMarkets: ['atlanta', 'chicago', 'orlando', 'dallas'],
  },
  chicago: {
    slug: 'chicago',
    name: 'Chicago',
    state: 'Illinois',
    stateAbbr: 'IL',
    metro: 'chicago',
    summary: 'Chicago listings can span conventions, corporate events, theater, festivals, sports, and union labor.',
    buyerSummary: 'Find stage labor, AV vendors, lighting companies, and production crew for Chicago events and venues.',
    workerSummary: 'Browse Chicago stagehand, AV, audio, lighting, video, rigging, and forklift-heavy convention gigs.',
    localInsight: 'Chicago calls may follow union or non-union hiring paths depending on the venue and production. Verify the applicable agreement and requirements before hiring or applying.',
    venues: ['McCormick Place', 'United Center', 'Navy Pier', 'Soldier Field', 'Chicago Theatre'],
    eventTypes: ['Trade shows', 'Corporate events', 'Theater', 'Concerts', 'Festivals'],
    topRoles: ['stagehands', 'av-technicians', 'riggers', 'audio-engineers', 'lighting-technicians'],
    topSpecialties: ['union-labor', 'av-rental', 'staging', 'lighting', 'audio'],
    nearbyMarkets: ['nashville', 'new-york', 'atlanta', 'dallas'],
  },
  dallas: {
    slug: 'dallas',
    name: 'Dallas',
    state: 'Texas',
    stateAbbr: 'TX',
    metro: 'dallas',
    summary: 'Dallas-Fort Worth hosts corporate conferences, concerts, sports productions, church events, and trade shows.',
    buyerSummary: 'Find AV companies, staging vendors, production teams, and event crew serving Dallas, Fort Worth, Arlington, and Plano.',
    workerSummary: 'Browse DFW stagehand, AV, audio, lighting, LED, video, and rigging gigs.',
    localInsight: 'Dallas-Fort Worth listings can include corporate campuses, worship venues, sports venues, concerts, and convention work.',
    venues: ['Kay Bailey Hutchison Convention Center', 'AT&T Stadium', 'American Airlines Center', 'Globe Life Field', 'Irving Convention Center'],
    eventTypes: ['Corporate events', 'Concerts', 'Sports events', 'Trade shows', 'Church events'],
    topRoles: ['stagehands', 'av-technicians', 'led-technicians', 'audio-engineers', 'riggers'],
    topSpecialties: ['av-rental', 'staging', 'lighting', 'video', 'production'],
    nearbyMarkets: ['houston', 'atlanta', 'nashville', 'chicago'],
  },
  houston: {
    slug: 'houston',
    name: 'Houston',
    state: 'Texas',
    stateAbbr: 'TX',
    searchLocation: 'Houston',
    summary: 'Houston is a strong corporate, medical, energy, sports, concert, and convention event market.',
    buyerSummary: 'Find AV vendors, production companies, staging providers, and event crew for Houston venues and corporate shows.',
    workerSummary: 'Browse Houston stagehand, AV, audio, lighting, video, LED, and rigging gigs.',
    localInsight: 'Energy, medical, and association events create steady corporate AV demand alongside sports and concert work.',
    venues: ['NRG Stadium', 'Toyota Center', 'George R. Brown Convention Center', 'Smart Financial Centre', 'House of Blues Houston'],
    eventTypes: ['Corporate events', 'Medical conventions', 'Concerts', 'Sports events', 'Trade shows'],
    topRoles: ['stagehands', 'av-technicians', 'audio-engineers', 'video-technicians', 'riggers'],
    topSpecialties: ['av-rental', 'production', 'lighting', 'video', 'staging'],
    nearbyMarkets: ['dallas', 'atlanta', 'nashville', 'orlando'],
  },
  atlanta: {
    slug: 'atlanta',
    name: 'Atlanta',
    state: 'Georgia',
    stateAbbr: 'GA',
    metro: 'atlanta',
    summary: 'Atlanta mixes film production, concerts, festivals, conventions, sports, and corporate events.',
    buyerSummary: 'Find event production companies, AV vendors, stage labor, and production crew in Atlanta.',
    workerSummary: 'Browse Atlanta gigs for stagehands, AV techs, grips, audio engineers, camera operators, and production assistants.',
    localInsight: 'Atlanta listings can span film work and live events, including grip, electric, camera, stagehand, and corporate AV roles.',
    venues: ['Georgia World Congress Center', 'State Farm Arena', 'Mercedes-Benz Stadium', 'Fox Theatre', 'Tabernacle'],
    eventTypes: ['Film and TV production', 'Corporate events', 'Concerts', 'Trade shows', 'Festivals'],
    topRoles: ['stagehands', 'av-technicians', 'camera-operators', 'audio-engineers', 'production-assistants'],
    topSpecialties: ['production', 'av-rental', 'lighting', 'audio', 'video'],
    nearbyMarkets: ['nashville', 'orlando', 'dallas', 'chicago'],
  },
  'new-york': {
    slug: 'new-york',
    name: 'New York',
    state: 'New York',
    stateAbbr: 'NY',
    metro: 'new-york',
    summary: 'New York is a dense live event market across theater, fashion, corporate events, broadcast, concerts, and trade shows.',
    buyerSummary: 'Find union labor, AV vendors, production companies, and show crew for New York events.',
    workerSummary: 'Browse New York gigs for stagehands, audio, lighting, video, camera, production, and theater crew.',
    localInsight: 'New York listings can include theater, fashion, corporate events, broadcast, concerts, and trade shows. Verify current volume and union requirements with each source.',
    venues: ['Madison Square Garden', 'Javits Center', 'Radio City Music Hall', 'Barclays Center', 'Lincoln Center'],
    eventTypes: ['Theater', 'Fashion events', 'Corporate events', 'Concerts', 'Broadcast'],
    topRoles: ['stagehands', 'audio-engineers', 'lighting-technicians', 'camera-operators', 'production-assistants'],
    topSpecialties: ['union-labor', 'production', 'av-rental', 'audio', 'lighting'],
    nearbyMarkets: ['chicago', 'atlanta', 'nashville', 'miami'],
  },
  miami: {
    slug: 'miami',
    name: 'Miami',
    state: 'Florida',
    stateAbbr: 'FL',
    metro: 'miami',
    summary: 'Miami listings can span festivals, art fairs, nightlife, fashion, corporate events, and cruise-adjacent production.',
    buyerSummary: 'Find AV companies, production vendors, lighting teams, audio companies, and event crew across Miami and South Florida.',
    workerSummary: 'Browse Miami stagehand, AV, audio, lighting, video, LED, and production gigs.',
    localInsight: 'Miami event calendars can include art fairs, music events, festivals, and corporate productions. Verify current dates, rates, and requirements with each listing.',
    venues: ['Miami Beach Convention Center', 'Kaseya Center', 'Hard Rock Stadium', 'Bayfront Park', 'Adrienne Arsht Center'],
    eventTypes: ['Music festivals', 'Art fairs', 'Fashion events', 'Corporate events', 'Nightlife events'],
    topRoles: ['stagehands', 'av-technicians', 'audio-engineers', 'lighting-technicians', 'video-technicians'],
    topSpecialties: ['production', 'av-rental', 'lighting', 'audio', 'video'],
    nearbyMarkets: ['orlando', 'atlanta', 'nashville', 'dallas'],
  },
  'san-francisco': {
    slug: 'san-francisco',
    name: 'San Francisco',
    state: 'California',
    stateAbbr: 'CA',
    metro: 'san-francisco',
    summary: 'San Francisco and the Bay Area are driven by tech conferences, corporate launches, concerts, sports, and hybrid event production.',
    buyerSummary: 'Find AV companies, video teams, LED support, production vendors, and crew across the Bay Area.',
    workerSummary: 'Browse San Francisco AV, video, audio, lighting, LED, stagehand, and camera gigs.',
    localInsight: 'Tech budgets reward polished corporate AV, streaming, LED, and large-format presentation experience.',
    venues: ['Moscone Center', 'Chase Center', 'Bill Graham Civic Auditorium', 'Oracle Park', 'SAP Center'],
    eventTypes: ['Tech conferences', 'Product launches', 'Corporate events', 'Concerts', 'Hybrid events'],
    topRoles: ['av-technicians', 'video-technicians', 'led-technicians', 'audio-engineers', 'camera-operators'],
    topSpecialties: ['av-rental', 'video', 'production', 'lighting', 'audio'],
    nearbyMarkets: ['los-angeles', 'orange-county', 'inland-empire', 'anaheim'],
  },
};

export const FEATURED_MARKET_SLUGS = Object.keys(MARKET_DATA);
export const FEATURED_HIRE_ROLE_SLUGS = [
  'stagehands',
  'av-technicians',
  'audio-engineers',
  'lighting-technicians',
  'video-technicians',
  'riggers',
  'led-technicians',
  'production-assistants',
];

export function getMarket(slug?: string): MarketData | undefined {
  return slug ? MARKET_DATA[slug] : undefined;
}

export function getSpecialty(slug?: string): VendorSpecialty | undefined {
  return slug ? VENDOR_SPECIALTIES[slug] : undefined;
}

export function getHireRole(slug?: string): HireRole | undefined {
  return slug ? HIRE_ROLES[slug] : undefined;
}

export function getGigRole(slug?: string): HireRole | undefined {
  return slug ? GIG_ROLES[slug] : undefined;
}

export function getVendorQuery(market: MarketData, specialty?: VendorSpecialty, limit = 60): string {
  const params = new URLSearchParams({ limit: String(limit), offset: '0' });
  if (market.region) {
    params.set('region', market.region);
  } else {
    params.set('city', market.searchLocation || market.name);
  }
  if (specialty) params.set('specialty', specialty.apiValue);
  return params.toString();
}

export function getJobQuery(market: MarketData, role?: HireRole, limit = 6): string {
  const params = new URLSearchParams({ limit: String(limit), offset: '0' });
  if (market.metro) {
    params.set('metro', market.metro);
  } else {
    params.set('location', market.searchLocation || market.name);
  }
  if (role) params.set('role', role.searchRole);
  return params.toString();
}
